import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import Toolbar from "@mui/material/Toolbar";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import NavigationAppIcon from "@mui/icons-material/Apps";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import Tooltip from "@mui/material/Tooltip";
import { pathOr } from "ramda";
import { styled } from "@mui/material/styles";

import Disclaimer from "~/components/Disclaimer";
import EventBus from "~/libs/eventBus";
import OutlineList from "~/components/OutlineList";
import BookmarkList from "./Content/BookmarkList";
import PageList from "./Content/PageList";

const StyledDrawer = styled(Drawer)(() => {
  return {
    // Disable drawer sliding transition
    "& .MuiDrawer-paper": {
      transition: "none !important"
    },
    "& .MuiDrawer-paperAnchorDockedLeft": {
      border: "none",
      boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px"
    }
  };
});

const StyledTabs = styled(Tabs)(props => {
  return {
    "& .MuiTabs-flexContainer": {
      borderBottom: "2px solid white"
    },
    "& .MuiTabs-indicator": {
      backgroundColor: props.theme.palette.mbred[0]
    }
  };
});

const StyledTab = styled(Tab)(props => ({
  "&.MuiTab-root": {
    minWidth: "initial",
    height: "46px",
    backgroundColor: props.selected ? props.theme.palette.mbred[0] : "#919191",
    color: "white !important"
  },
  "&:focus-visible": {
    // "--dsb-outline-color": "green",
    outline: props.theme.outline(),
    outlineOffset: "-2px"
  }
}));

const StyledToolbar = styled(Toolbar)(props => {
  const minHeight = props.isSingleTab ? "46px" : "48px";
  const height = props.isSingleTab ? "46px" : "48px";

  return {
    minHeight,
    height,
    backgroundColor: props.theme.palette.mbred[0],
    color: "white",
    fontSize: props.theme.typography.pxToRem(18),
    [props.theme.breakpoints.up("sm")]: {
      minHeight,
      height
    }
    // "@media (min-width: 0px)": {
    //   "@media (orientation: landscape)": {
    //     minHeight,
    //     height
    //   }
    // }
  };
});

class LeftSidebar extends Component {
  static whyDidYouRender = false;

  constructor(props) {
    super(props);

    this.onTabChange = this.onTabChange.bind(this);
    this.getDrawerSlideProps = this.getDrawerSlideProps.bind(this);
    this.getDrawerPaperProps = this.getDrawerPaperProps.bind(this);
    this.getDrawerVariant = this.getDrawerVariant.bind(this);
    this.zoomToFitAgain = this.zoomToFitAgain.bind(this);
    this.getCurrentTab = this.getCurrentTab.bind(this);
  }

  onTabChange(event, value) {
    this.props.switchTabOnSidebarLeft(value);
  }

  zoomToFitAgain() {
    EventBus.dispatch("zoomToFitAgain", "main");
    EventBus.dispatch("zoomToFitAgain", "embedded");
  }

  getDrawerSlideProps() {
    const { isOpen } = this.props;

    return {
      in: isOpen,
      timeout: 450,
      onEntering: this.zoomToFitAgain,
      onExiting: this.zoomToFitAgain
    };
  }

  getDrawerPaperProps() {
    const { isOpen } = this.props;
    return { style: { width: isOpen ? 300 : 0 } };
  }

  getDrawerVariant() {
    const { isDocked } = this.props;
    return isDocked ? "persistent" : "temporary";
  }

  getCurrentTab() {
    const { activeTab, activeTabs, tabNames } = this.props;

    let currentTab = activeTab;

    if (activeTabs[activeTab] === false) {
      currentTab = tabNames[0];
    }

    return currentTab;
  }

  render() {
    const {
      isOpen,
      toggleSidebarLeft,
      isLeftSidebarVisible,
      showPlannerTab,
      showPagesTab,
      showOutlineTab,
      showBookmarksTab,
      isSingleTab,
      outline,
      offset,
      pages,
      closeSidebarLeft,
      goToPage
    } = this.props;

    const currentTab = this.getCurrentTab();

    const outlineNodesOpenDefault = pathOr(
      false,
      ["book", "initial", "sidebarLeft", "bookmarks", "isOpen"],
      window.getClientConfiguration()
    );

    return (
      isLeftSidebarVisible && (
        <StyledDrawer
          anchor="left"
          className="leftSidebar"
          open={isOpen}
          variant={this.getDrawerVariant()}
          onClose={toggleSidebarLeft}
          slotProps={{
            paper: this.getDrawerPaperProps(),
            transition: this.getDrawerSlideProps()
          }}
        >
          <StyledTabs
            value={currentTab}
            onChange={this.onTabChange}
            variant="fullWidth"
            onKeyDown={event => {
              event.stopPropagation();
            }}
          >
            {showPlannerTab && <StyledTab icon={<CardTravelIcon hidden={isSingleTab} />} value="planner" />}

            {showPagesTab && (
              <StyledTab
                id="st-tab-0"
                aria-controls="st-tabpanel-0"
                icon={
                  <Tooltip title="Miniatur-Seitenansicht">
                    <NavigationAppIcon hidden={isSingleTab} />
                  </Tooltip>
                }
                value="pages"
              />
            )}

            {showOutlineTab && (
              <StyledTab
                id="st-tab-1"
                aria-controls="st-tabpanel-1"
                icon={
                  <Tooltip title="Inhaltsverzeichnis">
                    <FormatAlignRightIcon hidden={isSingleTab} />
                  </Tooltip>
                }
                value="outline"
              />
            )}

            {showBookmarksTab && (
              <StyledTab
                id="st-tab-2"
                aria-controls="st-tabpanel-2"
                sx={{
                  icon: {
                    color: "white"
                  }
                }}
                icon={
                  <Tooltip title="Lesezeichen">
                    <BookmarkBorderIcon hidden={isSingleTab} />
                  </Tooltip>
                }
                value="bookmarks"
              />
            )}
          </StyledTabs>
          {currentTab === "planner" && <StyledToolbar isSingleTab={isSingleTab}>Unterrichtsplaner</StyledToolbar>}
          {currentTab === "pages" && (
            <>
              <StyledToolbar isSingleTab={isSingleTab}>Seiten</StyledToolbar>
              <StyledPageList />
            </>
          )}
          {currentTab === "outline" && (
            <>
              <StyledToolbar isSingleTab={isSingleTab}>Inhaltsverzeichnis</StyledToolbar>
              <StyledOutlineList
                onNodeClick={goToPage}
                outline={outline}
                pages={pages}
                onSidebarClose={closeSidebarLeft}
                offset={offset}
                openDefault={outlineNodesOpenDefault}
              />
            </>
          )}
          {currentTab === "bookmarks" && (
            <>
              <StyledToolbar isSingleTab={isSingleTab}>Lesezeichen</StyledToolbar>
              <StyledBookmarkList />
            </>
          )}
          <StyledDisclaimer />
        </StyledDrawer>
      )
    );
  }
}

const StyledDisclaimer = styled(Disclaimer)(props => ({
  height: props.theme.typography.pxToRem(36),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgb(179, 179, 179)",
  borderRadius: 0
}));

const StyledPageList = styled(PageList)(props => ({
  flex: 1,
  overflow: "auto",
  "&:focus-visible": {
    outline: props.theme.outline(),
    outlineOffset: "-2px"
  }
}));

const StyledOutlineList = styled(OutlineList)(props => ({
  flex: 1,
  overflow: "auto",
  "&:focus-visible": {
    outline: props.theme.outline(),
    outlineOffset: "-2px"
  }
}));

const StyledBookmarkList = styled(BookmarkList)(props => ({
  flex: 1,
  overflow: "auto",
  "&:focus-visible": {
    outline: props.theme.outline(),
    outlineOffset: "-2px"
  }
}));

LeftSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isDocked: PropTypes.bool.isRequired,
  isLeftSidebarVisible: PropTypes.bool.isRequired,
  toggleSidebarLeft: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  switchTabOnSidebarLeft: PropTypes.func.isRequired,
  activeTabs: PropTypes.object,
  showPlannerTab: PropTypes.bool.isRequired,
  showPagesTab: PropTypes.bool.isRequired,
  showOutlineTab: PropTypes.bool.isRequired,
  showBookmarksTab: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  tabNames: PropTypes.array,
  isSingleTab: PropTypes.bool.isRequired,
  outline: PropTypes.array,
  offset: PropTypes.number,
  pages: PropTypes.array,
  closeSidebarLeft: PropTypes.func.isRequired,
  goToPage: PropTypes.func.isRequired
};

export default LeftSidebar;
